import { Box, Grid, IconButton, Paper } from "@mui/material";
import SvgIcon from "@mui/material/SvgIcon";
import { grey } from "@mui/material/colors";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  GeolocateControl,
  Layer,
  Map,
  MapProvider,
  NavigationControl,
  Popup,
  ScaleControl,
  Source,
} from "react-map-gl";
import { RecoilRoot, useRecoilState } from "recoil";
import {
  currentViewState,
  enabledResetButtonState,
  filterState,
} from "../context/states";
import { theme } from "../styles/theme";
import CreateNewEntry from "./CreateNewEntry";
import MapBandModal from "./MapBandModal";
import MapFilterLegendContainer from "./MapFilterLegendContainer";
import {
  ClusterCountLayer,
  ClusterLayer,
  RegionBorderLayer,
  RegionDensityLayer,
  UnclusteredPointLayer,
} from "./MapLayers";
import MapLocationModal from "./MapLocationModal";
import ShowHideSidebarButton from "./ShowHideSidebarButton";
import SidebarContainer from "./SidebarContainer";
import TextFieldStyled from "./TextFieldStyled";
import FullViewIcon from "./icons/FullViewIcon";
import SearchIcon from "./icons/SearchIcon";
import ShrinkIcon from "./icons/ShrinkIcon";
//import Map from 'react-map-gl/maplibre';
import { generateZoomBasedOnWidth, initialMapView } from "../styles/mapStyles";

export default function PopMap({
  mapboxAccessToken,
  mapStyles = "mapbox://styles/julianpletz/cl3bpunqj003014rx59y2nb6d?optimize=true",
  locations,
  showViewSwitch = false,
  categories,
  regions,
  landkreise,
  zipcodes,
  headerHeight,
  onFullscreenToggled,
  contentFullscreen,
  mapSkin,
  clusterStyles,
  createLocationUrl,
}) {
  return (
    <RecoilRoot>
      <PopMapImpl
        mapboxAccessToken={mapboxAccessToken}
        mapStyles={mapStyles}
        locations={locations}
        showViewSwitch={showViewSwitch}
        categories={categories}
        regions={regions}
        landkreise={landkreise}
        zipcodes={zipcodes}
        headerHeight={headerHeight}
        onFullscreenToggled={onFullscreenToggled}
        contentFullscreen={contentFullscreen}
        mapSkin={mapSkin}
        clusterStyles={clusterStyles}
        createLocationUrl={createLocationUrl}
      />
    </RecoilRoot>
  );
}

function PopMapImpl({
  mapboxAccessToken,
  mapStyles,
  locations,
  showViewSwitch,
  categories,
  regions,
  landkreise,
  zipcodes,
  headerHeight,
  onFullscreenToggled,
  contentFullscreen,
  mapSkin,
  clusterStyles,
  createLocationUrl,
}) {
  const mapRef = useRef(null);
  const [filter, setFilter] = useRecoilState(filterState);
  const [enabledResetButton, setEnabledResetButton] = useRecoilState(
    enabledResetButtonState,
  );
  const [locationsToDisplay, setLocationsToDisplay] = useState(locations);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [selectedRegion, setSelectedRegion] = useState(null);
  const [selectedBand, setSelectedBand] = useState(null);
  const hoveredFeature = useRef(null);
  const [sidebarOpen, setSidebarOpen] = useState(
    !window.matchMedia("(max-width: 899px)").matches,
  );
  const [currentView, setCurrentView] = useRecoilState(currentViewState);
  const [pointDetail, setPointDetail] = useState(false);
  const [showBandDetails, setShowBandDetails] = useState(false);

  const bounds = [
    [-2.988281, 48.100095],
    [17.918701, 54.635697],
  ];

  // throw an error if no mapboxAccessToken is provided
  if (!mapboxAccessToken) {
    throw new Error("No mapboxAccessToken provided");
  }

  const regionPolygongs = useMemo(() => {
    if (!landkreise || landkreise.length === 0) return null;

    return {
      type: "FeatureCollection",
      name: "regions",
      features: landkreise.map((region) => {
        const bands =
          region.popboard_bands?.filter(
            (b) =>
              b.display_on_map === true ||
              b.source === "popboard" ||
              b.source === null,
          ) ?? [];

        return {
          type: "Feature",
          id: region.id,
          properties: {
            id: region.id,
            region: region,
            type: "regions",
            density: bands?.length ?? 0,
            bands: bands ?? [],
          },
          state: {
            id: region.id,
          },
          geometry: region.geometry,
        };
      }),
    };
  }, [landkreise]);

  const markers = useMemo(() => {
    if (!locationsToDisplay) {
      return;
    }

    return {
      type: "FeatureCollection",
      name: "locations",
      features: locationsToDisplay.map((location) => {
        return {
          type: "Feature",
          geometry: {
            type: "Point",
            coordinates: [
              location.lng ? location.lng : location.zipcode.lng,
              location.lat ? location.lat : location.zipcode.lat,
            ],
          },
          properties: {
            id: location.id,
            name: location.name,
            location: location,
            cluster: false,
            icon: location.category?.icon,
            color: location.category?.color,
            type: "locations",
          },
        };
      }),
    };
  }, [locationsToDisplay]);

  useEffect(() => {
    if (!locations || locations.length === 0) return;

    const matcher = filter.name ? new RegExp(filter.name, "i") : null;

    setLocationsToDisplay(
      locations
        .filter((location) => {
          if (!filter || Object.keys(filter).length === 0) return true;

          let matched = false;

          if (matcher) {
            if (matcher.test(location.name)) matched = true;
            if (matcher.test(location.address)) matched = true;
            if (matcher.test(location.category?.name)) matched = true;
            if (matcher.test(location.zipcode?.zipcode)) matched = true;
          } else {
            matched = true;
          }

          if (!matched) return false;

          if (filter.categories && filter.categories.length > 0) {
            matched &=
              filter.categories.filter(
                (c) => c.id + "" === location.category?.id + "",
              ).length > 0;
          } else if (filter.categories && filter.categories.length === 0) {
            matched = false;
          }

          if (!matched) return false;

          if (filter.regionZipcodes) {
            matched &=
              filter.regionZipcodes.filter((z) => z.id === location.zipcode?.id)
                .length > 0;
          }

          return matched;
        })
        .sort((a, b) => {
          if (!filter.zipcode) {
            return a.name.localeCompare(b.name);
          }

          if (a.zipcode?.id === b.zipcode?.id) {
            // Same ZIP as filter should be on top
            return a.name.localeCompare(b.name);
          }

          return a.zipcode?.zipcode === filter.zipcode ? -1 : 1;
        }),
    );
  }, [locations, filter]);

  useEffect(() => {
    setTimeout(initHandlers, 250);
  }, [mapRef, mapRef.current]);

  const initHandlers = () => {
    if (!mapRef.current) {
      setTimeout(initHandlers, 250);
      return;
    }

    const map = mapRef.current.getMap();

    map.on("mousemove", RegionDensityLayer.id, handleMouseMoved);
    map.on("mousemove", ClusterLayer().id, handleMouseMoved);
    map.on("mousemove", UnclusteredPointLayer.id, handleMouseMoved);

    map.on("mouseleave", RegionDensityLayer.id, handleMouseLeaved);
    map.on("mouseleave", ClusterLayer().id, handleMouseLeaved);
    map.on("mouseleave", UnclusteredPointLayer.id, handleMouseLeaved);

    const observer = new ResizeObserver(() => {
      try {
        map.resize();
      } catch (e) {
        console.info("Error resizing map", e);
      }
    });
    observer.observe(document.querySelector("#map"));
    return () => {
      observer.disconnect();
    };
  };

  const [viewState, setViewState] = useState({
    ...initialMapView,
    zoom: initialMapView.zoom ?? generateZoomBasedOnWidth(6.5),
  });

  const setView = (view) => {
    const map = mapRef.current.getMap();

    map.resize();
    setCurrentView(view);
    if (view === "bands") {
      map.setLayoutProperty("clusters", "visibility", "none");
      map.setLayoutProperty("cluster-count", "visibility", "none");
      map.setLayoutProperty("unclustered-point", "visibility", "none");
      map.setLayoutProperty("region-density", "visibility", "visible");
    } else if (view === "locations") {
      map.setLayoutProperty("clusters", "visibility", "visible");
      map.setLayoutProperty("cluster-count", "visibility", "visible");
      map.setLayoutProperty("unclustered-point", "visibility", "visible");
      map.setLayoutProperty("region-density", "visibility", "none");
    }
  };

  const handleMouseMoved = (event) => {
    const feature = event.features[0];

    if (!feature) return;

    if (
      [
        RegionDensityLayer.id,
        UnclusteredPointLayer.id,
        ClusterCountLayer().id,
        ClusterLayer().id,
      ].find((id) => id === feature.layer?.id)
    ) {
      if (hoveredFeature.current && hoveredFeature.current.properties.type) {
        mapRef.current.setFeatureState(
          {
            source: hoveredFeature.current.properties.type,
            id: hoveredFeature.current.properties.id,
          },
          { hover: false },
        );
      }

      hoveredFeature.current = feature;

      if (feature.properties.type) {
        mapRef.current.setFeatureState(
          {
            source: feature.properties.type,
            id: feature.properties.id,
          },
          { hover: true },
        );
      }

      mapRef.current.getCanvas().style.cursor = "pointer";
    }
  };

  const handleMouseLeaved = (event) => {
    if (!hoveredFeature.current) return;

    mapRef.current.getCanvas().style.cursor = "";

    if (hoveredFeature.current.properties.type) {
      mapRef.current.setFeatureState(
        {
          source: hoveredFeature.current.properties.type,
          id: hoveredFeature.current.properties.id,
        },
        { hover: false },
      );
    }

    hoveredFeature.current = null;
  };

  const onClick = (event) => {
    const feature = event.features[0];

    if (feature?.layer?.id === ClusterLayer().id) {
      const clusterId = feature.properties.cluster_id;
      const mapboxSource = mapRef.current.getSource("locations");
      mapboxSource.getClusterExpansionZoom(clusterId, (err, zoom) => {
        if (err) return;
        mapRef.current.easeTo({
          center: feature.geometry.coordinates,
          zoom: zoom,
          duration: 500,
        });
      });
    } else if (feature?.layer?.id === UnclusteredPointLayer.id) {
      setSelectedLocation(JSON.parse(feature.properties.location));
    } else {
      // Clicked on a region?
      const features = mapRef.current
        .getMap()
        .queryRenderedFeatures(event.point, {
          layers: [RegionDensityLayer.id],
        });

      if (!features || features.length === 0) return;

      const clickedFeature = features[0];
      const r = {
        lng: event.lngLat.lng,
        lat: event.lngLat.lat,
        region: JSON.parse(clickedFeature.properties.region),
      };

      setSelectedRegion(r);
    }
  };

  const handleFullscreenToggled = () => {
    if (onFullscreenToggled && typeof onFullscreenToggled === "function") {
      onFullscreenToggled();
    }
  };

  const handleLocationSelected = (location) => {
    const isMobile = window.matchMedia("(max-width: 899px)").matches;

    if (isMobile) {
      setSidebarOpen(false);
    }
    setTimeout(() => {
      setSelectedLocation(location);
    }, 1500);
  };

  const handlePointDetailClosed = () => {
    setPointDetail(false);
    setSelectedLocation(null);
  };

  const handleBandDetailClosed = () => {
    setShowBandDetails(false);
  };

  const handleInputChange = (event) => {
    const inputValue = event.target.value;
    setFilter({ ...filter, name: inputValue });

    if (inputValue.trim() !== "") {
      setSidebarOpen(true);
    }
  };

  return (
    <>
      <MapProvider>
        <Paper variant="search-wrapper" sx={searchWrapper}>
          <Grid container spacing={1}>
            <Grid item xs md>
              <Box className="search">
                {currentView === "locations" && (
                  <TextFieldStyled
                    placeholder="Suche nach Orten, Kategorie oder Adresse "
                    onChange={handleInputChange}
                    startAdornment={<SearchIcon />}
                  />
                )}
              </Box>
            </Grid>
            <Grid item xs="auto">
              <Grid container spacing={1}>
                <Grid
                  item
                  xs="auto"
                  sx={{ display: { xs: "none", md: "block" } }}
                >
                  <CreateNewEntry createLocationUrl={createLocationUrl} />
                </Grid>
                <Grid item xs="auto" className="fullscreen-button">
                  <IconButton
                    aria-label="fullsize"
                    size="small"
                    variant="square-secondary"
                    onClick={handleFullscreenToggled}
                  >
                    <SvgIcon>
                      {contentFullscreen ? <ShrinkIcon /> : <FullViewIcon />}
                    </SvgIcon>
                  </IconButton>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Paper>
        <Box sx={mapContainerStyles(contentFullscreen, headerHeight)}>
          <Box sx={backdropStyles(sidebarOpen)} />
          <Grid container sx={{ height: "100%", overflow: "hidden" }}>
            <Grid item xs={12} md={4} xl={3} sx={sidebarStyles(sidebarOpen)}>
              <SidebarContainer
                markers={markers}
                bands={
                  selectedRegion?.region?.popboard_bands.filter(
                    (b) =>
                      b.display_on_map === true ||
                      b.source === "popboard" ||
                      b.source === null,
                  ) ?? []
                }
                regions={landkreise}
                currentView={currentView}
                handleLocationSelected={handleLocationSelected}
                sidebarOpen={sidebarOpen}
                setSidebarOpen={setSidebarOpen}
                setView={setView}
                showViewSwitch={showViewSwitch}
                pointDetail={pointDetail}
                showBandDetails={showBandDetails}
                setShowBandDetails={setShowBandDetails}
                handlePointDetailClosed={handlePointDetailClosed}
                handleBandDetailClosed={handleBandDetailClosed}
                selectedLocation={selectedLocation}
                selectedRegion={selectedRegion}
                setSelectedBand={setSelectedBand}
                selectedBand={selectedBand}
              />
              {sidebarOpen && (
                <ShowHideSidebarButton
                  theme={theme}
                  onClick={() => setSidebarOpen(false)}
                />
              )}
            </Grid>
            <Grid
              item
              xs={12}
              md={sidebarOpen ? 8 : 12}
              xl={sidebarOpen ? 9 : 12}
            >
              <Map
                id="map"
                mapboxAccessToken={mapboxAccessToken}
                initialViewState={{
                  ...viewState,
                  maxBounds: bounds,
                  minZoom: 5,
                  maxZoom: 15,
                }}
                onMove={() => {
                  setEnabledResetButton(true);
                }}
                onClick={onClick}
                mapStyle={mapStyles}
                interactiveLayerIds={[
                  ClusterLayer().id,
                  UnclusteredPointLayer.id,
                ]}
                ref={mapRef}
              >
                <MapFilterLegendContainer
                  categories={categories}
                  regions={regions}
                  zipcodes={zipcodes}
                  contentFullscreen={contentFullscreen}
                  currentView={currentView}
                />

                <GeolocateControl position="bottom-right" />
                <ScaleControl position="bottom-right" />
                <NavigationControl position="bottom-right" />

                <Source
                  id="locations"
                  type="geojson"
                  data={markers}
                  cluster={true}
                  clusterMaxZoom={14}
                  clusterRadius={50}
                >
                  <Layer {...ClusterLayer(mapSkin)} />
                  <Layer {...ClusterCountLayer(clusterStyles)} />
                  <Layer {...UnclusteredPointLayer} />
                  {/*markers?.features?.map((feature) => (
                    <Marker
                      key={feature.properties.id}
                      longitude={feature.geometry.coordinates[0]}
                      latitude={feature.geometry.coordinates[1]}
                    >
                      <LocationMarker feature={feature} />
                    </Marker>
                  ))*/}
                </Source>

                <Source id="regions" type="geojson" data={regionPolygongs}>
                  <Layer {...RegionDensityLayer} />
                  <Layer {...RegionBorderLayer} />
                </Source>

                {selectedRegion && (
                  <Popup
                    anchor="top"
                    longitude={selectedRegion.lng}
                    latitude={selectedRegion.lat}
                    onClose={() => setSelectedRegion(null)}
                    closeOnClick={false}
                  >
                    <MapBandModal selectedRegion={selectedRegion} />
                  </Popup>
                )}

                {selectedLocation && (
                  <Popup
                    anchor="top"
                    longitude={
                      selectedLocation.properties?.location.lng ??
                      selectedLocation.lng ??
                      selectedLocation.zipcode.lng
                    }
                    latitude={
                      selectedLocation.properties?.location.lat ??
                      selectedLocation.lat ??
                      selectedLocation.zipcode.lat
                    }
                    onClose={() => {
                      handleLocationSelected(null);
                      setPointDetail(false);
                    }}
                  >
                    <MapLocationModal
                      selectedLocation={
                        selectedLocation.properties?.location ??
                        selectedLocation
                      }
                      setPointDetail={setPointDetail}
                      setSidebarOpen={setSidebarOpen}
                    />
                  </Popup>
                )}
              </Map>
              {!sidebarOpen && (
                <ShowHideSidebarButton
                  theme={theme}
                  onClick={() => setSidebarOpen(true)}
                  sidebarClosed
                />
              )}
            </Grid>
          </Grid>
        </Box>
        <Box
          sx={{
            display: contentFullscreen ? "none" : { xs: "block", md: "none" },
            margin: "0.5rem 0.5rem 0 0.5rem",
          }}
        >
          <CreateNewEntry createLocationUrl={createLocationUrl} />
        </Box>
      </MapProvider>
    </>
  );
}

const backdropStyles = (sidebarOpen) => {
  return {
    [theme.breakpoints.down("md")]: {
      display: sidebarOpen ? "block" : "none",
    },
    [theme.breakpoints.up("md")]: {
      display: "none",
    },
    zIndex: "55",
    backgroundColor: "rgba(0,0,0,0.75)",
    height: "100%",
    width: "100%",
    top: "0",
    left: "0",
    bottom: "0",
    right: "0",
    position: "absolute",
  };
};

const sidebarStyles = (sidebarOpen) => {
  return {
    display: sidebarOpen ? "block" : "none",
    height: "100%",
    width: "100%",
    position: "absolute",
    [theme.breakpoints.up("md")]: {
      position: "relative",
    },
  };
};

const mapContainerStyles = (contentFullscreen) => {
  return {
    position: "relative",
    overflow: "hidden",
    aspectRatio: "4 / 3",
    width: "100%",
    height: contentFullscreen ? `calc(100dvh - 4.0625rem - 3.625rem)` : "24rem",
    [theme.breakpoints.up("sm")]: {
      height: contentFullscreen
        ? `calc(100dvh - 4.0625rem - 3.625rem)`
        : "auto",
    },
  };
};

const searchWrapper = {
  padding: "0.5rem",
  backgroundColor: grey[300],
  minHeight: "3.625rem",
  "& .search": {
    "& .MuiInputBase-root": {
      backgroundColor: "white",
      height: "2.875rem",
    },
    "& .MuiInputBase-input": {
      paddingLeft: "0 !important",
    },
    "& .MuiFormControl-root": {
      marginBottom: "0",
    },
    "& .MuiFormLabel-root": {
      marginBottom: "0",
    },
  },
};
